/**
 * CertificadorRoute — gate da superfície de certificação (E52.F3, ADR-0063 §2).
 *
 * Espelha o gate COMPOSTO do servidor: escritório-plataforma **e** papel
 * `certificador`. O titular da plataforma não herda esta tela, porque não herda
 * o poder de assinatura que ela antecede (BR-08). Fail-closed enquanto o perfil
 * resolve; o backend continua sendo a autoridade real (403).
 *
 * E70.F1: falha ao ler o perfil vira estado de erro com "tentar novamente",
 * não `/403` — mesma regra do `PlatformRoute`.
 */
import { type ReactElement, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { resetCurrentRoleCache, useCurrentRole } from '@/lib/auth/useCurrentRole';
import { GuardIndisponivel } from './GuardIndisponivel';

function Guard({ children, onRetry }: { children: ReactElement; onRetry: () => void }) {
  const { role, isPlatform, loading, error } = useCurrentRole();
  if (loading) {
    return <div data-testid="certificador-guard-loading" />;
  }
  if (error) {
    return <GuardIndisponivel onRetry={onRetry} />;
  }
  if (!isPlatform || role !== 'certificador') {
    return <Navigate to="/403" replace />;
  }
  return children;
}

export function CertificadorRoute({ children }: { children: ReactElement }): ReactElement {
  const [tentativa, setTentativa] = useState(0);
  const retry = () => {
    resetCurrentRoleCache();
    setTentativa((atual) => atual + 1);
  };
  return (
    <Guard key={tentativa} onRetry={retry}>
      {children}
    </Guard>
  );
}
